/**
 * pageRerunMessage — 검수 창 [이 페이지 다시 OCR]의 결과를 한 문장으로 (순수 함수).
 *
 * 근거: 회신 `docs/ocr-hub-v2/BE-20260914-reply-page-reocr-force.md`(dev `3435dc0`·`84186de`).
 * 이 버튼은 `force: true` 로 부른다 — 완료된 쪽도 다시 읽는다. 서버가 보장하는 것은
 * 「파일 + 한 쪽 범위가 함께 와야만 허용 · 호출 1회 = 최대 1쪽」(§2)이다.
 *
 * 갈래는 앞에서부터 본다. 앞 갈래가 뒤 갈래를 가린다.
 *   1. 새로 쪼갠 쪽이 있다 — 서버가 범위를 다르게 읽었다. 2026-08-23 전체 실행 사고와 같은 모양이라
 *      빨갛게 말한다. 서버가 막는다고 해도 화면은 계속 잰다(패널이 실행 전후 쪽 목록을 잰다).
 *   2. 서버가 그 파일에서 오류를 냈다 — 응답은 200 이어도 results[].error 로 온다. 그 말을 그대로 전한다.
 *   3. 대기열에 두 쪽 이상 — 「호출 1회 = 최대 1쪽」과 다르다. 1과 같은 이유로 빨갛게.
 *   4. 대기열 0 · 건너뛴 쪽 있음 — 그 쪽이 이미 읽는 중이다(처리중 150 은 force 여도 건너뛴다, §2 표).
 *      고장이 아니라 기다리면 되는 일이라 파랗게 말한다.
 *   5. 대기열 0 · 건너뛴 쪽 없음 — 서버가 그 쪽을 찾지 못했다(쪽수 밖 범위는 200 + target_pages [] — ocrApi.js).
 *   6. 대기열 1 — 정상. 목록에 반영하려면 ③ 매칭을 다시 해야 한다(§2 끝 문단).
 *
 * 종전 문구 「이미 읽은 페이지는 건너뛰는 것으로 보입니다」는 없앴다(§4-3) — 이제 완료된 쪽은 건너뛰지 않는다.
 *
 * newPages: { total, byFile: { [fileId]: page_index[] } } | null — null 은 「못 쟀다」이지 「없다」가 아니다.
 * 반환: { type: 'info' | 'error', text }
 */
export function pageRerunMessage({ pageNo, queued = 0, skipped = 0, errors = [], newPages = null }) {
  if (newPages && newPages.total > 0) {
    // 패널은 대상 파일만이 아니라 **전체 파일**을 재서 준다(서버가 file_id를 무시했는지 보려는 것).
    // page_index는 파일 안에서 0부터라 합쳐서 쪽 번호를 찍으면 A파일 1쪽과 B파일 1쪽이 같은 「1」로
    // 뭉개진다. 파일이 여럿이면 쪽 번호 대신 파일 수를 말한다 — 「어디를 봐야 하는가」가 더 쓸모 있다.
    const byFile = newPages.byFile || {};
    const fileCount = Object.keys(byFile).length;
    let what;
    if (fileCount > 1) {
      what = `파일 ${fileCount}개에서 ${newPages.total}쪽이 새로 쪼개졌습니다`;
    } else {
      const nos = Object.values(byFile).flat().map((i) => i + 1).sort((a, b) => a - b);
      const head = nos.slice(0, 3).join(', ') + (nos.length > 3 ? ` 외 ${nos.length - 3}쪽` : '쪽');
      what = `${newPages.total}쪽이 새로 쪼개졌습니다(${head})`;
    }
    return {
      type: 'error',
      text: `${pageNo}쪽 한 장만 다시 읽으려 했는데 ${what}. `
        + `서버가 범위나 file_id를 다르게 해석했을 수 있습니다 — OCR 대기열 ${queued}페이지. `
        + '의도한 실행이 아니라면 백엔드에 대기열 정리를 요청하세요.',
    };
  }
  if (errors.length > 0) {
    return { type: 'error', text: `${pageNo}쪽을 다시 읽지 못했습니다 — 서버 오류: ${errors[0]}` };
  }
  if (queued > 1) {
    return {
      type: 'error',
      text: `${pageNo}쪽 한 장만 다시 읽으려 했는데 OCR 대기열에 ${queued}쪽이 들어갔습니다. `
        + '서버는 한 번에 한 쪽만 받기로 되어 있습니다 — 의도한 실행이 아니라면 백엔드에 대기열 정리를 요청하세요.',
    };
  }
  if (queued === 0) {
    return skipped > 0
      ? { type: 'info', text: `${pageNo}쪽은 지금 읽는 중이라 건너뛰었습니다 — 끝난 뒤 다시 누르세요.` }
      : { type: 'error', text: `${pageNo}쪽을 서버에서 찾지 못해 다시 읽지 않았습니다 — 이 파일의 쪽수를 확인하세요.` };
  }
  const unmeasured = newPages ? '' : ' 새로 쪼갠 쪽이 있는지는 확인하지 못했습니다.';
  return {
    type: 'info',
    text: `${pageNo}쪽을 다시 읽도록 대기열에 넣었습니다. 읽기가 끝나면(② 진행률) ③ 문제 매칭을 다시 실행하세요`
      + ` — 매칭해야 빠졌던 문제가 목록에 생깁니다.${unmeasured}`,
  };
}
